import { bcs } from '@mysten/sui/bcs';
import {
  fromBase64,
  normalizeStructTag,
  normalizeSuiAddress,
  SUI_TYPE_ARG,
} from '@mysten/sui/utils';

import {
  type ParsedArgument,
  type ParsedCallArg,
  type ParsedCommand,
  type ParsedSplitCommand,
  SendAssertionError,
  type SendExpectation,
} from './transaction-validation';

export const isOwnedObjectInput = (arg: ParsedArgument, inputs: ParsedCallArg[]): boolean =>
  arg.$kind === 'Input' && inputs[arg.Input]?.$kind === 'Object';

/**
 * A merge may only fold the user's own input coins into another of their input
 * coins. Any other destination or source moves value outside the approved
 * shape: into the split output (inflating the transfer), into the gas coin
 * (to the sponsor), or out of the split output before it is transferred.
 */
export function assertMergeSources(commands: ParsedCommand[], inputs: ParsedCallArg[]): void {
  for (const command of commands) {
    if (command.$kind !== 'MergeCoins') continue;
    const { destination, sources } = command.MergeCoins;
    if (![destination, ...sources].every((arg) => isOwnedObjectInput(arg, inputs))) {
      throw new SendAssertionError('it merges coins outside your own input coins');
    }
  }
}

/**
 * The split must draw from one of the user's input coins, or from the gas coin
 * only in the one shape the backend builds for it: a self-paid SUI send.
 */
export function assertSplitSource(
  split: ParsedSplitCommand,
  inputs: ParsedCallArg[],
  gasOwner: string,
  expected: SendExpectation,
): void {
  const { coin } = split.SplitCoins;
  if (isOwnedObjectInput(coin, inputs)) return;
  if (coin.$kind !== 'GasCoin') {
    throw new SendAssertionError('it splits a coin that is not one of your input coins');
  }

  const selfPaid = normalizeSuiAddress(gasOwner) === normalizeSuiAddress(expected.sender);
  const approvedSui = normalizeStructTag(expected.coinType) === normalizeStructTag(SUI_TYPE_ARG);
  if (!selfPaid || !approvedSui) {
    throw new SendAssertionError('it splits the gas coin, which is not your approved coin');
  }
}

export function readPureBytes(arg: ParsedArgument, inputs: ParsedCallArg[]): Uint8Array | null {
  if (arg.$kind !== 'Input') return null;
  const input = inputs[arg.Input];
  if (input?.$kind !== 'Pure') return null;
  return fromBase64(input.Pure.bytes);
}

export function assertSplitAmount(
  splitCommand: ParsedSplitCommand,
  inputs: ParsedCallArg[],
  expectedAmount: bigint,
): void {
  const { amounts } = splitCommand.SplitCoins;
  const [firstAmount] = amounts;
  if (amounts.length !== 1 || !firstAmount) {
    throw new SendAssertionError('it splits an unexpected number of amounts');
  }
  const amountBytes = readPureBytes(firstAmount, inputs);
  if (!amountBytes) throw new SendAssertionError('the amount is not a literal value');
  if (BigInt(bcs.U64.parse(amountBytes)) !== expectedAmount) {
    throw new SendAssertionError('it sends a different amount');
  }
}

export function isCommandResult(arg: ParsedArgument, index: number): boolean {
  return (
    (arg.$kind === 'Result' && arg.Result === index) ||
    (arg.$kind === 'NestedResult' && arg.NestedResult[0] === index && arg.NestedResult[1] === 0)
  );
}

export function assertTransferRecipient(
  arg: ParsedArgument,
  inputs: ParsedCallArg[],
  recipient: string,
): void {
  const addressBytes = readPureBytes(arg, inputs);
  if (!addressBytes) throw new SendAssertionError('the recipient is not a literal address');
  if (normalizeSuiAddress(bcs.Address.parse(addressBytes)) !== normalizeSuiAddress(recipient)) {
    throw new SendAssertionError('it pays a different recipient');
  }
}
